import React from 'react';
import CampaignWrapper from './CampaignWrapper';
import LanguageLearningWrapper from './LanguageLearningWrapper';
import HumanTaskWrapper from './HumanTaskWrapper';

/**
 * Configuration for app layout tabs by discussing object type.
 * Each discussing object type defines its available tabs and the component to render.
 */

export interface AppTab {
  id: string;
  label: string;
  icon?: string; // Icon name or component reference
}

export interface AppLayoutConfig {
  tabs: AppTab[];
  defaultTab: string;
  renderComponent: (props: AppLayoutComponentProps) => React.ReactNode;
}

export interface AppLayoutComponentProps {
  tabId: string;
  threadId?: string;
  discussingObjectMatch: Record<string, any>;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
}

/**
 * Render function for Campaign - uses CampaignWrapper which handles
 * both draft (shows CampaignPlan) and launched (shows tab content).
 * CampaignWrapper calls onTabsReady once it knows which tabs to show.
 */
const renderCampaignComponent = ({
  tabId,
  threadId,
  discussingObjectMatch,
  updateThreadViewVisualDescription,
  onTabsReady,
}: AppLayoutComponentProps): React.ReactNode => {
  const campaignId = discussingObjectMatch?.campaignId;

  if (!campaignId) {
    return <div></div>;
  }

  return (
    <CampaignWrapper
      campaignId={campaignId}
      threadId={threadId}
      tabId={tabId}
      updateThreadViewVisualDescription={updateThreadViewVisualDescription}
      onTabsReady={onTabsReady}
    />
  );
};

/**
 * Render function for HumanTask - uses HumanTaskWrapper which handles
 * phone call tasks (with dialer) and web action tasks (details only).
 */
const renderHumanTaskComponent = ({
  tabId,
  threadId,
  discussingObjectMatch,
  updateThreadViewVisualDescription,
  onTabsReady,
}: AppLayoutComponentProps): React.ReactNode => {
  const taskId = discussingObjectMatch?.taskId;

  if (!taskId) {
    return <div></div>;
  }

  return (
    <HumanTaskWrapper
      taskId={taskId}
      threadId={threadId}
      tabId={tabId}
      updateThreadViewVisualDescription={updateThreadViewVisualDescription}
      onTabsReady={onTabsReady}
    />
  );
};

/**
 * Render function for LanguageLearning - uses LanguageLearningWrapper which handles
 * different thread types (vocab, learning, reading) with appropriate tabs.
 */
const renderLanguageLearningComponent = ({
  tabId,
  threadId,
  discussingObjectMatch,
  updateThreadViewVisualDescription,
  onTabsReady,
}: AppLayoutComponentProps): React.ReactNode => {
  const languageLearningId = discussingObjectMatch?.languageLearningId;
  const threadType = discussingObjectMatch?.threadType;

  if (!languageLearningId || !threadType) {
    return <div></div>;
  }

  return (
    <LanguageLearningWrapper
      languageLearningId={languageLearningId}
      threadType={threadType}
      threadId={threadId}
      tabId={tabId}
      updateThreadViewVisualDescription={updateThreadViewVisualDescription}
      onTabsReady={onTabsReady}
    />
  );
};

/**
 * App layout configurations by discussing object type.
 * Campaign has no header tabs - CampaignWrapper handles tabs internally for launched campaigns.
 * LanguageLearning has no header tabs - LanguageLearningWrapper handles tabs based on thread type.
 */
export const APP_LAYOUT_CONFIGS: Record<string, AppLayoutConfig> = {
  Campaign: {
    tabs: [], // No header tabs - CampaignWrapper shows its own tabs when launched
    defaultTab: '',
    renderComponent: renderCampaignComponent,
  },
  LanguageLearning: {
    tabs: [], // No header tabs - LanguageLearningWrapper shows tabs based on thread type
    defaultTab: '',
    renderComponent: renderLanguageLearningComponent,
  },
  HumanTask: {
    tabs: [], // No header tabs - HumanTaskWrapper shows tabs based on task type
    defaultTab: 'details',
    renderComponent: renderHumanTaskComponent,
  },
};

/**
 * Default app layout config for threads without a specific discussing object.
 * Uses empty tabs - canvas assets will open in modals.
 */
export const DEFAULT_APP_LAYOUT_CONFIG: AppLayoutConfig = {
  tabs: [],
  defaultTab: '',
  renderComponent: () => null,
};

/**
 * Get the app layout config for a discussing object type.
 * Returns default config if no specific config exists (all threads use app layout).
 */
export const getAppLayoutConfig = (
  discussingObject: string | undefined,
): AppLayoutConfig | undefined => {
  if (discussingObject && APP_LAYOUT_CONFIGS[discussingObject]) {
    return APP_LAYOUT_CONFIGS[discussingObject];
  }
  // Return default config for all threads - enables app layout with modals
  return DEFAULT_APP_LAYOUT_CONFIG;
};

/**
 * Check if a discussing object type has an app layout configuration.
 */
export const hasAppLayout = (discussingObject: string | undefined): boolean => {
  return !!getAppLayoutConfig(discussingObject);
};
